// donmeals-global.js
// Shared nav, menu drawer, and cart floating-panel logic.
// Cart is wired to Shopify's real /cart API — persists across pages.

function menuOpen() {
  const drawer = document.getElementById('menuDrawer');
  const lines  = document.getElementById('menuLines');
  if (drawer.classList.contains('open')) {
    menuClose();
  } else {
    drawer.classList.add('open');
    lines.classList.add('open');
    document.body.style.overflow = 'hidden';
  }
}
function menuClose() {
  document.getElementById('menuDrawer').classList.remove('open');
  document.getElementById('menuLines').classList.remove('open');
  document.body.style.overflow = '';
}

document.addEventListener('click', function(e) {
  const drawer = document.getElementById('menuDrawer');
  const lines  = document.getElementById('menuLines');
  if (!drawer || !lines) return;
  if (drawer.classList.contains('open') &&
      !drawer.contains(e.target) &&
      !lines.closest('.site-nav__btn').contains(e.target)) {
    menuClose();
  }
});

// Free delivery threshold, by cart subtotal (was 10 meals by count).
const FREE_SHIP_MIN = 225;

// The local delivery fee, matching Settings > Shipping and delivery >
// Local delivery. It is only ever charged on an order that is actually
// being delivered — see chargesDelivery() below.
const DELIVERY_FEE = 14.99;

// Whether this cart gets billed for delivery at all. Two ways it doesn't,
// and the panel used to bill for delivery in both of them:
//
//   Pickup — the shopper picks it up themselves. Shopify has local pickup
//   enabled on the kitchen, so there is a free Pick up option at checkout,
//   but this panel kept showing the delivery fee anyway and the total it
//   quoted was wrong by the whole fee.
//
//   Gift cards — a cart of nothing but gift cards has no physical goods.
//   Shopify already skips shipping on those (requires_shipping is false on
//   every line), so nobody was ever actually charged, but the panel quoted
//   a fee on the way to checkout, which reads exactly like one.
// What is being taken off this cart, and by what. Cart-level applications
// carry the code's own title; per-line ones are folded in so an automatic
// product discount gets named too.
function dmDiscountInfo(cart) {
  const original = cart.original_total_price != null ? cart.original_total_price : cart.total_price;
  const amount = Math.max(0, (original - cart.total_price) / 100);
  const titles = [];
  (cart.cart_level_discount_applications || []).forEach(function (a) {
    if (a && a.title && titles.indexOf(a.title) === -1) titles.push(a.title);
  });
  (cart.items || []).forEach(function (i) {
    (i.discounts || []).forEach(function (d) {
      if (d && d.title && titles.indexOf(d.title) === -1) titles.push(d.title);
    });
  });
  return { amount: amount, label: titles.length ? 'Discount · ' + titles.join(', ') : 'Discount' };
}

function dmChargesDelivery(items) {
  if (!items || items.length === 0) return false;
  return !items.every(function (i) { return i.requires_shipping === false; });
}

// ── The cart we already know about ───────────────────────────────
// Every cart interaction used to end with its own trip to /cart.js before
// anything moved on screen: an add cost two round trips, and a +/- on a
// card cost three (read the cart, change the line, read it back). The
// endpoints already hand back what is needed — /cart/change.js answers
// with the whole cart, /cart/add.js with the line it added — so the panel
// repaints from that straight away and only reconciles with the server in
// the background.
let dmCart = null;

async function fetchCart() {
  const res = await fetch('/cart.js');
  dmCart = await res.json();
  return dmCart;
}

// Folds the lines /cart/add.js just returned into the cached cart, so the
// meal can be drawn in the panel without asking the server what the cart
// looks like now. Totals are recomputed from the lines rather than nudged,
// so a repeated add can't drift; the background reconcile settles anything
// only the server knows, like an automatic discount.
function dmMergeAddedLines(data) {
  if (!dmCart || !dmCart.items || !data) return;
  const added = data.items || (data.key ? [data] : []);
  added.forEach(function (line) {
    const existing = dmCart.items.find(function (i) { return i.key === line.key; });
    if (existing) {
      Object.assign(existing, line);
    } else {
      dmCart.items.push(line);
    }
  });
  dmCart.item_count = dmCart.items.reduce(function (n, i) { return n + i.quantity; }, 0);
  dmCart.total_price = dmCart.items.reduce(function (n, i) {
    return n + (i.final_line_price != null ? i.final_line_price : (i.line_price || 0));
  }, 0);
  dmCart.original_total_price = dmCart.items.reduce(function (n, i) {
    return n + (i.line_price != null ? i.line_price : (i.final_line_price || 0));
  }, 0);
  dmCart.total_discount = Math.max(0, dmCart.original_total_price - dmCart.total_price);
}

// Paint now from what is known, then quietly correct it.
async function dmPaintCart() {
  if (dmCart && dmCart.items) {
    await cartRender(dmCart);
    fetchCart().then(function (cart) { cartRender(cart); }).catch(function () {});
    return;
  }
  await cartRender();
}

async function cartAddItem(variantId, quantity = 1, properties = null) {
  const item = { id: variantId, quantity };
  if (properties) item.properties = properties;
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: [item] })
  });
  if (!res.ok) {
    let detail = res.status + '';
    try {
      const err = await res.json();
      detail = err.description || err.message || detail;
    } catch (_) {}
    throw new Error(detail);
  }
  const data = await res.json();
  dmMergeAddedLines(data);
  return data;
}

// Same endpoint, form-encoded instead of JSON, and able to drop the macro
// line-item properties. Shopify accepts both bodies; the published theme
// uses the form path and adds fine, while this theme only ever used the
// JSON path. When the JSON add is refused, addToCart() walks down to these
// so the shopper still gets their meal, and logs which one worked.
async function cartAddForm(variantId, quantity, properties) {
  const body = new URLSearchParams();
  body.append('id', String(variantId));
  body.append('quantity', String(quantity));
  if (properties) {
    Object.keys(properties).forEach(function (k) {
      const v = properties[k];
      if (v !== '' && v != null) body.append('properties[' + k + ']', v);
    });
  }
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Accept': 'application/json'
    },
    body: body.toString()
  });
  if (!res.ok) {
    let detail = res.status + '';
    try {
      const err = await res.json();
      detail = err.description || err.message || detail;
    } catch (_) {}
    throw new Error(detail);
  }
  const data = await res.json();
  dmMergeAddedLines(data);
  return data;
}

// Answers with the whole cart, so callers can repaint from the return
// value instead of reading /cart.js back.
async function cartChangeItem(lineKey, quantity) {
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: lineKey, quantity })
  });
  const cart = await res.json();
  if (cart && cart.items) dmCart = cart;
  return cart;
}

async function cartOpen() {
  const drawer = document.getElementById('cartDrawer');
  if (drawer.classList.contains('cart-drawer--docked')) {
    drawer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    await cartRender();
    return;
  }
  if (drawer.classList.contains('open')) {
    cartClose();
  } else {
    drawer.classList.add('open');
    document.getElementById('cartOverlay').classList.add('open');
    document.body.style.overflow = 'hidden';
    await cartRender();
  }
}

function cartClose() {
  const drawer = document.getElementById('cartDrawer');
  if (drawer && drawer.classList.contains('cart-drawer--docked')) return;
  drawer.classList.remove('open');
  document.getElementById('cartOverlay').classList.remove('open');
  document.body.style.overflow = '';
}

async function cartRender(cart) {
  if (!cart || !cart.items) cart = await fetchCart();
  const items = cart.items;
  const qty   = cart.item_count;
  // Subtotal is BEFORE discounts, the total AFTER. total_price is already
  // discounted, so using it for the Subtotal row printed the discount
  // underneath without ever taking it off.
  const gross = (cart.original_total_price != null ? cart.original_total_price : cart.total_price) / 100;
  const sub   = cart.total_price / 100;
  const billed = dmChargesDelivery(items);
  // No delivery figure is added here any more. Bird sets the rate from the
  // choice made on /cart, so a fee guessed in the drawer was both wrong for
  // pickup and inconsistent with the cart page.
  const total = sub;

  const badge = document.getElementById('cartBadge');
  if (badge) { badge.textContent = qty; badge.dataset.count = qty; }

  const countEl = document.getElementById('cartCount');
  if (countEl) countEl.textContent = qty + ' item' + (qty !== 1 ? 's' : '');

  const shipPct = Math.min(100, Math.round((sub / FREE_SHIP_MIN) * 100));
  const shipLeft = Math.max(0, FREE_SHIP_MIN - sub);
  const shipMsgHtml = shipLeft > 0
    ? 'Add <strong>$' + shipLeft.toFixed(2) + '</strong> more for <strong>Free Delivery</strong>'
    : '&#127881; <strong>Free Delivery unlocked!</strong>';

  const shipWrap = document.querySelector('.cart-drawer__ship');
  if (shipWrap) shipWrap.style.display = billed ? '' : 'none';
  const shipFill = document.getElementById('cartShipFill');
  if (shipFill) shipFill.style.width = shipPct + '%';
  const shipMsg = document.getElementById('cartShipMsg');
  if (shipMsg) shipMsg.innerHTML = shipMsgHtml;

  const subEl  = document.getElementById('cartSub');
  if (subEl) subEl.textContent = '$' + gross.toFixed(2);

  // Name whatever is taking money off this cart. Codes can arrive without
  // being typed — a /discount/CODE link, an email, a popup — and Shopify
  // applies them silently, so the only honest thing the panel can do is
  // show the amount and say where it came from.
  const disc = dmDiscountInfo(cart);
  const discRow = document.getElementById('cartDiscountRow');
  if (discRow) discRow.style.display = disc.amount > 0 ? '' : 'none';
  const discLabel = document.getElementById('cartDiscountLabel');
  if (discLabel) discLabel.textContent = disc.label;
  const discAmt = document.getElementById('cartDiscountAmt');
  if (discAmt) discAmt.textContent = '-$' + disc.amount.toFixed(2);
  const shipLabel = document.getElementById('cartShipLabel');
  if (shipLabel) shipLabel.textContent = billed ? 'Delivery' : 'Shipping';
  const shipEl = document.getElementById('cartShip');
  if (shipEl) {
    // Pickup is free and delivery is $14.99, and which one this is gets
    // decided at checkout — so the cart quotes a figure only when it is
    // free either way, and says where the answer comes from otherwise.
    shipEl.textContent = !billed ? 'FREE' : 'At checkout';
    shipEl.style.color = !billed ? '#3F8F27' : '#8A8A8A';
  }
  const totEl  = document.getElementById('cartTot');
  if (totEl) totEl.textContent = '$' + total.toFixed(2);

  // Mobile sticky bottom bar (real Shopify discount data, no fabrication)
  const originalTotal = (cart.original_total_price != null ? cart.original_total_price : cart.total_price) / 100;
  const discount = (cart.total_discount || 0) / 100;
  const mcbMeals = document.getElementById('mcbMeals');
  const mcbSub = document.getElementById('mcbSub');
  const mcbDiscountStat = document.getElementById('mcbDiscountStat');
  const mcbDiscount = document.getElementById('mcbDiscount');
  const mcbShip = document.getElementById('mcbShip');
  const mcbTotal = document.getElementById('mcbTotal');
  const mobileBar = document.getElementById('mobileCartBar');
  if (mcbMeals) mcbMeals.textContent = qty;
  if (mcbSub) {
    mcbSub.textContent = '$' + originalTotal.toFixed(2);
    mcbSub.classList.toggle('mcb-stat__val--strike', discount > 0);
  }
  if (mcbDiscountStat) mcbDiscountStat.style.display = discount > 0 ? 'flex' : 'none';
  if (mcbDiscount) mcbDiscount.textContent = '-$' + discount.toFixed(2);
  if (mcbShip) mcbShip.textContent = !billed ? 'FREE' : 'At checkout';
  const mcbShipLabel = document.getElementById('mcbShipLabel');
  if (mcbShipLabel) mcbShipLabel.textContent = billed ? 'Delivery' : 'Shipping';
  const mcbShipFill = document.getElementById('mcbShipFill');
  if (mcbShipFill) mcbShipFill.style.width = shipPct + '%';
  const mcbShipMsg = document.getElementById('mcbShipMsg');
  if (mcbShipMsg) mcbShipMsg.innerHTML = shipMsgHtml;
  const mcbShipBar = document.getElementById('mcbShipBar');
  if (mcbShipBar) {
    mcbShipBar.style.display = billed ? '' : 'none';
    mcbShipBar.classList.toggle('mcb-ship--done', shipLeft <= 0);
  }
  if (mcbTotal) mcbTotal.textContent = '$' + total.toFixed(2);
  if (mobileBar) mobileBar.classList.toggle('is-visible', qty > 0);

  const footer = document.getElementById('cartFooter');
  if (footer) footer.style.display = items.length ? 'block' : 'none';
  const mealCount = document.getElementById('cartMealCount');
  const mealNum   = document.getElementById('cartMealNum');
  if (mealCount) mealCount.style.display = items.length ? 'flex' : 'none';
  if (mealNum)   mealNum.textContent = qty;

  const note = document.getElementById('cartNoteInput');
  if (note && document.activeElement !== note) note.value = cart.note || '';

  const list = document.getElementById('cartItems');
  const empty = document.getElementById('cartEmpty');

  if (!items.length) {
    if (list) {
      list.innerHTML = '';
      if (empty) { list.appendChild(empty); empty.style.display = 'flex'; }
    }
    syncCardStates(cart);
    return;
  }

  const itemsHtml = items.map(item => {
    const cal     = (item.properties && item.properties['_cal'])     || '—';
    const protein = (item.properties && item.properties['_protein']) ? item.properties['_protein'] + 'g' : '—';
    const carbs   = (item.properties && item.properties['_carbs'])   ? item.properties['_carbs'] + 'g'   : '—';
    const fat     = (item.properties && item.properties['_fat'])     ? item.properties['_fat'] + 'g'     : '—';

    return `
    <div class="cart-drawer__item" data-key="${item.key}">
      <img src="${item.image}" class="cart-drawer__item-img" alt="${item.product_title}">
      <div class="cart-drawer__item-mid">
        <div class="cart-drawer__item-name">${item.product_title}</div>
        ${item.variant_title && item.variant_title !== 'Default Title' ? `<div style="font-size:11px;color:#8A8A8A;margin-top:-4px;">${item.variant_title}</div>` : ''}
        <div class="cart-drawer__item-stats">
          <div class="cart-drawer__stat"><span class="cart-drawer__stat__val">${cal}</span><span class="cart-drawer__stat__lbl">Cal</span></div>
          <div class="cart-drawer__stat"><span class="cart-drawer__stat__val">${protein}</span><span class="cart-drawer__stat__lbl">Protein</span></div>
          <div class="cart-drawer__stat"><span class="cart-drawer__stat__val">${carbs}</span><span class="cart-drawer__stat__lbl">Carbs</span></div>
          <div class="cart-drawer__stat"><span class="cart-drawer__stat__val">${fat}</span><span class="cart-drawer__stat__lbl">Fat</span></div>
        </div>
      </div>
      <div class="cart-drawer__item-right">
        <div class="cart-drawer__qty">
          <button class="cart-drawer__qty-btn" onclick="cartQtyClick(this, -1)">&#8722;</button>
          <span class="cart-drawer__qty-num">${item.quantity}</span>
          <button class="cart-drawer__qty-btn" onclick="cartQtyClick(this, 1)">+</button>
        </div>
        <button class="cart-drawer__remove" onclick="cartRemove('${item.key}')" aria-label="Remove item">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 4.5h10M6.5 4.5V3a1 1 0 011-1h1a1 1 0 011 1v1.5M6 7.5v4M10 7.5v4M4 4.5l.6 8.2a1 1 0 001 .8h4.8a1 1 0 001-.8l.6-8.2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>`;
  }).join('');

  if (list) list.innerHTML = itemsHtml;
  syncCardStates(cart);
}

// ── Quantity change in the cart drawer: update the number on screen
// instantly (no waiting on the network), then debounce the actual
// /cart/change.js call so rapid taps only fire one request once the
// user pauses — this is what makes +/- feel lightning fast. ──
const cartQtyTimers = {};

function cartQtyClick(btn, delta) {
  const itemEl = btn.closest('.cart-drawer__item');
  if (!itemEl) return;
  const lineKey = itemEl.dataset.key;
  const numEl = itemEl.querySelector('.cart-drawer__qty-num');
  const current = parseInt((numEl && numEl.textContent) || '1', 10) || 0;
  const next = Math.max(current + delta, 0);
  if (numEl) numEl.textContent = next;
  cartQty(lineKey, next);
}

function cartQty(lineKey, newQty) {
  clearTimeout(cartQtyTimers[lineKey]);
  cartQtyTimers[lineKey] = setTimeout(async () => {
    try {
      const cart = await cartChangeItem(lineKey, newQty);
      await cartRender(cart);
    } catch (e) {
      console.error('cartQty error:', e);
    }
  }, 300);
}

async function cartRemove(lineKey) {
  clearTimeout(cartQtyTimers[lineKey]);
  const cart = await cartChangeItem(lineKey, 0);
  await cartRender(cart);
}

function findCard(btn) {
  return btn.closest('.col-card');
}

function setCardState(card, qty) {
  const btn = card.querySelector('.col-card__btn');
  const qtyWrap = card.querySelector('.col-card__qty');
  const qtyNum = card.querySelector('.col-qty-num');
  if (qty > 0) {
    if (btn) btn.style.display = 'none';
    if (qtyWrap) qtyWrap.classList.add('col-card__qty--active');
    if (qtyNum) qtyNum.textContent = qty;
  } else {
    if (btn) btn.style.display = '';
    if (qtyWrap) qtyWrap.classList.remove('col-card__qty--active');
    if (qtyNum) qtyNum.textContent = 1;
  }
}

// Surfaces failures where the shopper is looking, in full. The previous
// version wrote the message into the button and cut it at 40 characters,
// which turned every Shopify cart error into the same unreadable stub —
// "The product 'Cheeseburger Bowl - Muscle" tells nobody anything.
function showCardError(card, btn, msg, detail) {
  if (btn) {
    btn.style.background = '#E5484D';
    btn.style.color = '#FFFFFF';
    setTimeout(() => { btn.style.background = ''; btn.style.color = ''; }, 3200);
  }
  if (!card) return;
  let el = card.querySelector('.col-card__error');
  if (!el) {
    el = document.createElement('div');
    el.className = 'col-card__error';
    el.style.cssText = 'margin-top:8px;font-size:11px;line-height:1.35;font-weight:600;color:#C2261C;';
    (card.querySelector('.col-card__btn-wrap') || card).appendChild(el);
  }
  el.textContent = msg;
  // On a preview/unpublished theme, print what was actually sent underneath
  // the message. Shopify's own error names the product but not the variant
  // id, so a stale id in cached markup and a genuine refusal from the shop
  // read identically. Shoppers on the live theme never see this.
  if (detail && !dmIsLiveTheme()) {
    const dbg = document.createElement('div');
    dbg.style.cssText = 'margin-top:4px;font-size:10px;font-weight:400;color:#8A8A8A;word-break:break-all;';
    dbg.textContent = detail;
    el.appendChild(dbg);
  }
  el.style.display = 'block';
  clearTimeout(el.hideTimer);
  el.hideTimer = setTimeout(() => { el.style.display = 'none'; }, 12000);
}

function dmIsLiveTheme() {
  try {
    return window.Shopify && window.Shopify.theme && window.Shopify.theme.role === 'main';
  } catch (e) {
    return true;
  }
}

// Re-reads the card's product from Shopify and returns the current id for
// the variant the card is showing. The grid prints variant ids straight
// into the markup, so a page served from cache — or a variant rebuilt in
// admin, which gives it a brand new id — leaves the DOM holding an id the
// shop no longer sells. That is what Shopify reports as "The product
// '...' does not exist."
async function resolveCardVariant(card) {
  const href = card.getAttribute('href') || '';
  const handle = (href.match(/\/products\/([^/?#]+)/) || [])[1];
  if (!handle) return null;
  const res = await fetch('/products/' + handle + '.js');
  if (!res.ok) return null;
  const product = await res.json();
  const wanted = (card.dataset.size || '').trim().toLowerCase();
  const match = product.variants.find(v => String(v.title).trim().toLowerCase() === wanted)
    || product.variants.find(v => v.available)
    || product.variants[0];
  return match ? String(match.id) : null;
}

// What the storefront itself says about a variant, straight from the same
// product JSON the theme's own pages are built from. Admin and Liquid can
// both call a meal sellable while /cart/add.js refuses it, and this is the
// view that settles which of the two the cart agrees with.
async function dmVariantInfo(card, variantId) {
  const href = card.getAttribute('href') || '';
  const handle = (href.match(/\/products\/([^/?#]+)/) || [])[1];
  if (!handle) return null;
  const res = await fetch('/products/' + handle + '.js', { cache: 'no-store' });
  if (!res.ok) return null;
  const product = await res.json();
  const v = product.variants.find(x => String(x.id) === String(variantId));
  return v ? { available: v.available, title: v.title } : null;
}

async function addToCart(btn) {
  const card = findCard(btn);
  let variantId = card && card.dataset.variantId;
  if (!variantId) {
    console.warn('addToCart: no data-variant-id on .col-card');
    showCardError(card, btn, 'This meal is missing its variant ID.');
    return;
  }

  setCardState(card, 1);

  const properties = {
    _cal: card.dataset.calories || '',
    _protein: card.dataset.protein || '',
    _carbs: card.dataset.carbs || '',
    _fat: card.dataset.fat || '',
  };

  try {
    await cartAddItem(variantId, 1, properties);
  } catch (e) {
    // The add failed. Before showing the shopper an error, work down the
    // ways this request can differ from the one the published theme sends,
    // which adds these same meals fine.
    let recovered = false;
    let recoveredVia = '';
    let storefrontAvailable = null;

    // 1. The same add, form-encoded rather than JSON.
    try {
      await cartAddForm(variantId, 1, properties);
      recovered = true;
      recoveredVia = 'form-encoded body';
    } catch (formError) {
      // 2. Form-encoded with the macro properties dropped. If this is the
      //    one that works, a line-item property is what Shopify objected
      //    to, not the meal.
      try {
        await cartAddForm(variantId, 1, null);
        recovered = true;
        recoveredVia = 'form-encoded body, no line-item properties';
      } catch (plainError) {
        // 3. The id in the markup may simply be out of date.
        try {
          const fresh = await resolveCardVariant(card);
          if (fresh && fresh !== String(variantId)) {
            await cartAddItem(fresh, 1, properties);
            card.dataset.variantId = fresh;
            variantId = fresh;
            recovered = true;
            recoveredVia = 'refreshed variant id';
          }
        } catch (retryError) {
          console.error('addToCart retry error:', retryError);
        }

        // 4. Ask the storefront what it thinks of this exact variant. The
        //    shop reporting the meal as available while the cart calls it
        //    sold out is a contradiction the product cannot cause: the two
        //    read the same record. What differs is the cart this browser is
        //    holding, so reset it and try once more. Emptying alone is not
        //    enough when the cart TOKEN is the bad part, so the cookie goes
        //    too and Shopify mints a fresh cart on the next call.
        if (!recovered) {
          try {
            const info = await dmVariantInfo(card, variantId);
            storefrontAvailable = info ? info.available : null;
            if (info && info.available) {
              const before = await fetchCart();
              const carried = (before && before.items) ? before.items.slice() : [];

              await fetch('/cart/clear.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
              });
              document.cookie = 'cart=; Max-Age=0; path=/';
              dmCart = null;

              await cartAddForm(variantId, 1, properties);
              recovered = true;
              recoveredVia = 'reset a stuck cart, then re-added';

              // Put back whatever the shopper already had, so the reset is
              // invisible to them rather than a silently emptied cart.
              for (const line of carried) {
                if (String(line.variant_id) === String(variantId)) continue;
                try {
                  await cartAddForm(line.variant_id, line.quantity, line.properties);
                } catch (restoreError) {
                  console.warn('addToCart: could not restore line', line.variant_id, restoreError);
                }
              }
            }
          } catch (clearError) {
            console.error('addToCart cart-reset error:', clearError);
          }
        }
      }
    }

    if (recovered) {
      console.warn('addToCart: first attempt refused, recovered via ' + recoveredVia, {
        variantId: variantId,
        shopifyMessage: (e && e.message) ? e.message : String(e)
      });
    }

    if (!recovered) {
      const handle = ((card.getAttribute('href') || '').match(/\/products\/([^/?#]+)/) || [])[1] || '?';
      console.error('addToCart failed', {
        variantId: variantId,
        productHandle: handle,
        shopifyMessage: (e && e.message) ? e.message : String(e),
        storefrontSaysAvailable: storefrontAvailable,
        properties: properties
      });
      setCardState(card, 0);
      showCardError(
        card,
        btn,
        (e && e.message) ? e.message : 'Could not add this meal.',
        'variant ' + variantId + ' · ' + handle +
          ' · storefront available: ' + String(storefrontAvailable)
      );
      return;
    }
  }

  const badge = document.getElementById('cartBadge');
  if (badge) { badge.classList.remove('pop'); void badge.offsetWidth; badge.classList.add('pop'); }
  await dmPaintCart();
}

async function updateQty(btn, delta) {
  const card = findCard(btn);
  const variantId = card && card.dataset.variantId;
  if (!variantId) return;

  const qtyEl = card.querySelector('.col-qty-num');
  const current = parseInt((qtyEl && qtyEl.textContent) || '1', 10) || 1;
  const next = Math.max(current + delta, 0);

  setCardState(card, next);

  try {
    // Cards add their meal with macro line properties, so the cart line's
    // key is "<variantId>:<hash>" rather than the bare variant id.
    // /cart/change.js matches on that key, so changing by variant id alone
    // could miss the line and leave the stepper doing nothing on screen.
    const cart = (dmCart && dmCart.items) ? dmCart : await fetchCart();
    const line = cart.items.find(i => String(i.variant_id) === String(variantId));
    if (line) {
      const updated = await cartChangeItem(line.key, next);
      await cartRender(updated);
    } else if (next > 0) {
      await addToCart(btn);
    } else {
      await cartRender();
    }
  } catch (e) {
    console.error('updateQty error:', e);
  }
}

function syncCardStates(cart) {
  document.querySelectorAll('.col-card[data-variant-id]').forEach(card => {
    const vid = card.dataset.variantId;
    const line = cart.items.find(i => String(i.variant_id) === String(vid));
    setCardState(card, line ? line.quantity : 0);
  });
}

// ── Upsell: "You may also like" carousel in the cart ──
async function upsellAdd(btn) {
  const variantId = btn.dataset.variantId;
  if (!variantId) return;
  const properties = {
    _cal: btn.dataset.calories || '',
    _protein: btn.dataset.protein || '',
    _carbs: btn.dataset.carbs || '',
    _fat: btn.dataset.fat || '',
  };
  const original = btn.textContent;
  btn.disabled = true;
  btn.textContent = '✓ Added';
  try {
    await cartAddItem(variantId, 1, properties);
    const badge = document.getElementById('cartBadge');
    if (badge) { badge.classList.remove('pop'); void badge.offsetWidth; badge.classList.add('pop'); }
    await dmPaintCart();
  } catch (e) {
    console.error('upsellAdd error:', e);
  } finally {
    setTimeout(() => { btn.disabled = false; btn.textContent = original; }, 900);
  }
}

function upsellScroll(dir) {
  const track = document.getElementById('cartUpsellTrack');
  if (track) track.scrollBy({ left: dir * 180, behavior: 'smooth' });
}

// ── Discount code: Shopify's cart AJAX API has no "apply discount"
// endpoint, so the code is carried through to checkout via the
// standard ?discount= URL param. ──
let cartDiscountCode = '';

function applyDiscountCode() {
  const input = document.getElementById('cartDiscountInput');
  const btn = document.getElementById('cartDiscountBtn');
  if (!input || !input.value.trim()) return;
  cartDiscountCode = input.value.trim();
  if (btn) {
    const original = btn.textContent;
    btn.textContent = '✓ Applied';
    setTimeout(() => { btn.textContent = original; }, 1400);
  }
}

// ── Special instructions: saved to the cart's note field ──
let cartNoteTimer = null;
function saveCartNote(value) {
  clearTimeout(cartNoteTimer);
  cartNoteTimer = setTimeout(async () => {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: value })
      });
    } catch (e) {
      console.error('saveCartNote error:', e);
    }
  }, 250);
}

// ── Collapsible conversion blocks (upsell / discount / note) ──
function toggleCartSection(id) {
  const wrap = document.getElementById(id);
  if (wrap) wrap.classList.toggle('is-open');
}

// ── Delivery or pickup, chosen in the cart ────────────────────
// Checkout can't be customised on this plan, so the shopper picks the
// method, the day, the window and (optionally) leaves a note here, and
// all four are written to cart attributes — they show on the order in
// Shopify Admin under Additional details. (Shopify's storefront APIs
// can't set real order *tags*; a one-line Shopify Flow rule turns any
// of these attributes into a tag if that's wanted.)
//
// Days and windows are the kitchen's published schedule. Change them
// here and both the date list and the time list follow:
//   Pickup    Sun–Wed, 12:00 PM – 8:00 PM
//   Delivery  Sun 12:00 PM – 8:00 PM; Mon–Wed 10 AM – 2 PM or 6 – 10 PM
const FF_DAYS = [0, 1, 2, 3];          // Sun, Mon, Tue, Wed
const FF_DATE_COUNT = 6;               // how many upcoming days to offer
const FF_PICKUP_ADDRESS = 'Unit 2 – 120 Lowson Crescent, Winnipeg';

function ffWindowsFor(method, dow) {
  if (dow === 0) return ['12:00 PM – 8:00 PM'];
  return ['10:00 AM – 2:00 PM', '6:00 PM – 10:00 PM'];
}

// How many minutes before a window opens ordering for it closes. 0 means
// a window stays orderable right up to its start — 5:05 PM still gets
// tonight's 6–10 PM. Raise this if the kitchen needs more notice; it is
// the only number that decides it.
const FF_LEAD_MINUTES = 0;

// Minutes past midnight for a window's start, read off its own label
// ('6:00 PM – 10:00 PM' → 1080). A label this can't parse returns null
// and is always offered, so a typo hides nothing.
function ffWindowStartMinutes(label) {
  const m = /^\s*(\d{1,2}):(\d{2})\s*(AM|PM)/i.exec(label);
  if (!m) return null;
  let h = Number(m[1]) % 12;
  if (/pm/i.test(m[3])) h += 12;
  return h * 60 + Number(m[2]);
}

function ffMinutesNow() {
  const n = new Date();
  return n.getHours() * 60 + n.getMinutes();
}

function ffIsToday(iso) {
  return iso === toISODate(new Date());
}

// Every window on a future day; on today, only the ones that haven't
// opened yet. Today used to be left off the date list entirely, so an
// order placed at 5 PM on a Wednesday was offered Sunday as the next
// slot while tonight's 6–10 PM window was still hours away.
function ffAvailableWindows(method, dow, isToday) {
  const all = ffWindowsFor(method, dow);
  if (!isToday) return all;
  const cutoff = ffMinutesNow() + FF_LEAD_MINUTES;
  return all.filter(w => {
    const start = ffWindowStartMinutes(w);
    return start === null || start > cutoff;
  });
}

const ffState = { method: 'delivery', date: '', dateLabel: '', time: '', note: '' };
// Set while the picker is being re-filled from the cart, so restoring a
// saved choice doesn't write a half-filled one back over it.
let ffRestoring = false;

// True when the cart came back holding a delivery slot that has since
// passed. Drives the one-off explanation in the picker's error line.
let ffStaleDate = false;

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDeliveryDate(d) {
  const label = d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  return ffIsToday(toISODate(d)) ? 'Today – ' + label : label;
}

// The next FF_DATE_COUNT service days, starting with today — today only
// counts while it still has a window nobody has missed.
function ffUpcomingDates() {
  const out = [];
  const cursor = new Date();
  for (let i = 0; i <= 28 && out.length < FF_DATE_COUNT; i++) {
    cursor.setTime(Date.now());
    cursor.setHours(0, 0, 0, 0);
    cursor.setDate(cursor.getDate() + i);
    if (FF_DAYS.indexOf(cursor.getDay()) === -1) continue;
    if (i === 0 && ffAvailableWindows(ffState.method, cursor.getDay(), true).length === 0) continue;
    out.push(new Date(cursor.getTime()));
  }
  return out;
}

// Rebuilt rather than built once: pickup and delivery close at different
// times of day, so whether today is still offered depends on the method.
// The shopper's date is kept if it survives the rebuild.
function ffBuildDates() {
  const select = document.getElementById('ffDate');
  if (!select) return;
  const previous = ffState.date;

  select.innerHTML = '';
  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.disabled = true;
  placeholder.selected = true;
  placeholder.textContent = 'Choose a date';
  select.appendChild(placeholder);

  ffUpcomingDates().forEach(d => {
    const opt = document.createElement('option');
    opt.value = toISODate(d);
    opt.dataset.dow = String(d.getDay());
    opt.textContent = formatDeliveryDate(d);
    select.appendChild(opt);
  });

  if (previous) {
    const match = select.querySelector('option[value="' + previous + '"]');
    if (match) {
      select.value = previous;
      ffState.dateLabel = match.textContent;
    } else {
      ffState.date = '';
      ffState.dateLabel = '';
    }
  }
}

// Time options depend on both the method and the weekday, so this runs
// again whenever either changes. A window the shopper already picked is
// kept selected when it still exists on the new day.
function ffBuildTimes() {
  const dateSelect = document.getElementById('ffDate');
  const timeSelect = document.getElementById('ffTime');
  if (!dateSelect || !timeSelect) return;

  const chosen = dateSelect.options[dateSelect.selectedIndex];
  const dow = chosen && chosen.dataset.dow ? Number(chosen.dataset.dow) : null;
  const previous = ffState.time;

  timeSelect.innerHTML = '';
  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.disabled = true;
  placeholder.selected = true;
  placeholder.textContent = dow === null ? 'Pick a date first' : 'Choose a time';
  timeSelect.appendChild(placeholder);

  if (dow === null) {
    timeSelect.disabled = true;
    ffState.time = '';
    return;
  }

  const windows = ffAvailableWindows(ffState.method, dow, ffIsToday(chosen.value));
  if (windows.length === 0) {
    placeholder.textContent = 'No windows left today';
    timeSelect.disabled = true;
    ffState.time = '';
    return;
  }

  timeSelect.disabled = false;
  windows.forEach(w => {
    const opt = document.createElement('option');
    opt.value = w;
    opt.textContent = w;
    timeSelect.appendChild(opt);
  });

  // One window on that day, or the same window as before — keep it chosen
  // so nobody has to re-pick something that never had an alternative.
  if (windows.length === 1) {
    timeSelect.value = windows[0];
    ffState.time = windows[0];
  } else if (previous && windows.indexOf(previous) !== -1) {
    timeSelect.value = previous;
    ffState.time = previous;
  } else {
    ffState.time = '';
  }
}

// The method tiles are gone — delivery vs pickup is chosen at checkout —
// so nothing switches the picker's wording any more. Kept as a no-op so an
// older cached copy of the markup calling it can't throw.
function ffSelectMethod() {}

function ffDateChanged(select) {
  ffState.date = select.value;
  const chosen = select.options[select.selectedIndex];
  ffState.dateLabel = chosen ? chosen.textContent : select.value;
  ffBuildTimes();
  ffClearError();
  ffSaveAttributes();
}

function ffTimeChanged(select) {
  ffState.time = select.value;
  ffClearError();
  ffSaveAttributes();
}

function ffNoteChanged(value) {
  ffState.note = (value || '').trim();
  ffSaveAttributes();
}

function ffClearError() {
  const wrap = document.getElementById('cartFulfillment');
  if (wrap && ffState.date && ffState.time) {
    ffStaleDate = false;
    wrap.classList.remove('cart-ff--error', 'cart-ff--shake');
  }
}

// Writes the whole choice in one call. The unused half is sent as an
// empty string, which is how the Cart API clears an attribute — so an
// order never carries both a pickup time and a delivery time.
let ffSaveTimer = null;
function ffSaveAttributes() {
  if (ffRestoring) return;
  const attributes = {
    // The shipping line Shopify puts on the order is the record of how it
    // is being fulfilled. The cart no longer writes a "Fulfillment" of its
    // own — that is what used to say "Store Pickup" on an order billed for
    // delivery. Blanks clear the old values off any cart still carrying them.
    'Fulfillment': '',
    'Pickup Location': '',
    'Pickup Date': '',
    'Pickup Time': '',
    'Pickup Note': '',
    'Delivery Date': ffState.dateLabel,
    'Delivery Time': ffState.time,
    'Delivery Note': ffState.note,
    // The label is what the shopper and the kitchen read, but it is not
    // stable: it is regenerated every day, so "Today – Thursday, Sep 24"
    // stops existing tomorrow. Restoring by label meant a cart left
    // overnight came back with no date, and Checkout then refused with
    // nothing but a shake. The ISO date is matched first. Leading
    // underscore keeps it off the order confirmation the customer sees.
    '_Date ISO': ffState.date
  };

  clearTimeout(ffSaveTimer);
  ffSaveTimer = setTimeout(async () => {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes })
      });
    } catch (e) {
      console.error('ffSaveAttributes error:', e);
    }
  }, 250);
}

// Re-reads whatever is already on the cart so the picker still shows the
// shopper's choice after a reload or a page change.
function ffRestore(cart) {
  ffRestoring = true;
  const a = (cart && cart.attributes) || {};
  // Carts saved before the method tiles came out still hold the pickup
  // keys, so read either side.
  const dateLabel = a['Delivery Date'] || a['Pickup Date'] || '';
  const time = a['Delivery Time'] || a['Pickup Time'] || '';
  const note = a['Delivery Note'] || a['Pickup Note'] || '';

  const savedISO = a['_Date ISO'] || '';
  const dateSelect = document.getElementById('ffDate');
  let matched = false;
  if (dateSelect && (savedISO || dateLabel)) {
    for (let i = 0; i < dateSelect.options.length; i++) {
      const opt = dateSelect.options[i];
      if (!opt.value) continue;
      if ((savedISO && opt.value === savedISO) ||
          (!savedISO && opt.textContent === dateLabel)) {
        dateSelect.selectedIndex = i;
        ffState.date = opt.value;
        // Take the label from the option, not from the cart: on the day
        // itself the option reads "Today – ..." and the saved one doesn't.
        ffState.dateLabel = opt.textContent;
        matched = true;
        break;
      }
    }
  }
  // A saved slot that no longer exists means the date has simply passed.
  // Clear it and say so, rather than leaving a dead Checkout button.
  if (!matched && (savedISO || dateLabel)) {
    ffState.date = '';
    ffState.dateLabel = '';
    ffStaleDate = true;
  }

  ffState.time = time || '';
  ffBuildTimes();
  const timeSelect = document.getElementById('ffTime');
  if (timeSelect && time) timeSelect.value = time;

  if (note) {
    ffState.note = note;
    const noteInput = document.getElementById('ffNote');
    if (noteInput) noteInput.value = note;
  }

  ffRestoring = false;
}

async function ffInit() {
  const wrap = document.getElementById('cartFulfillment');
  if (!wrap) return;
  ffBuildDates();
  ffBuildTimes();
  try {
    const res = await fetch('/cart.js');
    if (res.ok) ffRestore(await res.json());
  } catch (e) { /* the picker still works, it just starts empty */ }
  if (ffStaleDate) {
    ffErrorText();
    if (wrap) wrap.classList.add('cart-ff--error');
  }
}

function ffErrorText() {
  const msg = document.getElementById('cartDeliveryErrorMsg');
  if (!msg) return;
  if (ffStaleDate && !ffState.date) {
    msg.textContent = 'Your slot has passed — choose a new date and time.';
  } else if (!ffState.date) {
    msg.textContent = 'Choose a date and a time to continue';
  } else {
    msg.textContent = 'Choose a time to continue';
  }
}

function attemptCheckout() {
  const wrap = document.getElementById('cartFulfillment');

  if (!ffState.date || !ffState.time) {
    ffErrorText();
    if (wrap) {
      wrap.classList.remove('cart-ff--shake');
      wrap.classList.add('cart-ff--error');
      void wrap.offsetWidth;
      wrap.classList.add('cart-ff--shake');
      wrap.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    const focusTarget = document.getElementById(ffState.date ? 'ffTime' : 'ffDate');
    if (focusTarget) focusTarget.focus();
    return;
  }

  if (wrap) wrap.classList.remove('cart-ff--error');

  let url = '/checkout';
  if (cartDiscountCode) url += '?discount=' + encodeURIComponent(cartDiscountCode);
  window.location.href = url;
}

document.addEventListener('DOMContentLoaded', async () => {
  try {
    await cartRender();
  } catch(e) {}
  ffInit();
});

if (window.innerWidth > 960) {
  let ticking = false;
  document.addEventListener('mousemove', e => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const x = (e.clientX / window.innerWidth  - 0.5) * 24;
      const y = (e.clientY / window.innerHeight - 0.5) * 24;
      document.querySelectorAll('.blob').forEach((b, i) => {
        const f = (i + 1) * 0.35;
        b.style.transition = 'transform 1.2s ease';
        b.style.transform  = `translate(${x * f}px, ${y * f}px)`;
      });
      ticking = false;
    });
  });
}
/* ══ BOTTOM STACK ══
   Three independent things compete for the bottom-right of a phone screen:
   this theme's mobile cart bar, a sticky promo CTA injected by an app, and
   Shopify Inbox's chat bubble. The old fix lifted chat by a hardcoded 128px,
   which was this bar's height and nothing else's — so when the app bar stacked
   on top, chat landed on it again.

   This measures instead of assuming. It finds every full-width element pinned
   to the bottom of the viewport, takes the highest top edge among them, and
   publishes the total height as --dm-bottom-stack for the chat rules to sit
   above. Apps can change their markup or their height and this still holds.

   Only full-width bars count (>= half the viewport): small floating buttons
   are not a stack, they are the thing being stacked. */
(function () {
  var CHAT_SEL = '#ShopifyChat,#shopify-chat,.shopify-chat-container,' +
                 'iframe[id*="chat" i],iframe[title*="chat" i]';
  var root = document.documentElement;
  var pending = false;

  function isChat(el) {
    try { return !!(el.closest && el.closest(CHAT_SEL)) || el.matches(CHAT_SEL); }
    catch (e) { return false; }
  }

  function measure() {
    pending = false;
    if (window.innerWidth > 1023) {
      root.style.setProperty('--dm-bottom-stack', '0px');
      return;
    }
    var vh = window.innerHeight;
    var highestTop = vh;
    var nodes = document.body ? document.body.querySelectorAll('*') : [];
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var cs;
      try { cs = getComputedStyle(el); } catch (e) { continue; }
      if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
      if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') continue;
      if (isChat(el)) continue;
      var r = el.getBoundingClientRect();
      if (r.height < 8) continue;
      if (r.width < window.innerWidth * 0.5) continue;   // a bar, not a button
      /* Anywhere in the bottom 40%: a stacked bar sits clear of the very
         bottom edge, so requiring a flush bottom would miss it. */
      if (r.bottom < vh * 0.6 || r.top >= vh) continue;
      if (r.top < highestTop) highestTop = r.top;
    }
    var h = Math.max(0, Math.round(vh - highestTop));
    if (h > vh * 0.6) h = Math.round(vh * 0.6);          // never shove chat off-screen
    root.style.setProperty('--dm-bottom-stack', h + 'px');
  }

  function schedule() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(measure);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
  addEventListener('load', schedule);
  addEventListener('resize', schedule);
  addEventListener('orientationchange', schedule);

  /* Apps inject their bars long after load, and the cart bar shows and hides
     with the cart, so watch for both rather than measuring once. */
  if (window.MutationObserver && document.body) {
    new MutationObserver(schedule).observe(document.body, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['class', 'style']
    });
  }
  [400, 1200, 2500, 5000].forEach(function (t) { setTimeout(schedule, t); });
})();
